import { useEffect, useRef, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Paperclip, FilePdf, File as FileIcon, X, UploadSimple } from "@phosphor-icons/react";

/** Attachment picker for an inbox reply: files already on the email / request / job, plus quotes, RAMS and completed
 *  forms rendered as PDFs — and an ad-hoc upload. `value` = [{id, label, meta, kind}]. */
export default function ReplyAttachmentPicker({ messageId, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState(null);
  const [limits, setLimits] = useState({ max_files: 10, max_total_mb: 20 });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    if (!open || groups) return;
    API.get(`/inbox/messages/${messageId}/attachable`).then((r) => { setGroups(r.data.groups); setLimits({ max_files: r.data.max_files, max_total_mb: r.data.max_total_mb }); }).catch(() => setGroups([]));
  }, [open, groups, messageId]);
  const picked = new Set(value.map((v) => v.id));
  const toggle = (item) => {
    if (picked.has(item.id)) return onChange(value.filter((v) => v.id !== item.id));
    if (value.length >= limits.max_files) return toast.error(`Up to ${limits.max_files} files per reply`);
    onChange([...value, item]);
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post(`/inbox/messages/${messageId}/reply-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...value, data]);
      toast.success(`${data.label} added`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed"); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div className="space-y-2" data-testid="inbox-reply-attachments">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="inbox-reply-attachment-chips">
          {value.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/30 px-2 py-0.5 text-xs" data-testid={`inbox-reply-chip-${a.id.replace(/[^a-zA-Z0-9_-]/g, "_")}`}>
              {a.kind === "pdf" ? <FilePdf size={12} className="text-red-500" /> : <FileIcon size={12} />} {a.label}{a.meta ? <span className="text-muted-foreground"> · {a.meta}</span> : null}
              <button type="button" onPointerDown={(e) => { e.preventDefault(); onChange(value.filter((v) => v.id !== a.id)); }} className="ml-0.5 text-muted-foreground hover:text-foreground" aria-label="Remove" data-testid={`inbox-reply-chip-remove-${a.id.replace(/[^a-zA-Z0-9_-]/g, "_")}`}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid="inbox-reply-attach-btn"><Paperclip size={13} className="mr-1" /> Attach{value.length ? ` (${value.length})` : ""}</Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[380px] p-0" data-testid="inbox-reply-attach-popover">
            <div className="max-h-[360px] overflow-y-auto p-2 space-y-3">
              {groups === null && <p className="p-3 text-xs text-muted-foreground">Loading files…</p>}
              {groups && groups.length === 0 && <p className="p-3 text-xs text-muted-foreground" data-testid="inbox-reply-attach-empty">Nothing on file yet — convert this email to a request (or upload a file below) to attach quotes, RAMS and job documents.</p>}
              {(groups || []).map((g) => (
                <div key={g.key} data-testid={`inbox-reply-attach-group-${g.key}`}>
                  <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</p>
                  <div className="space-y-0.5">
                    {g.items.map((it) => (
                      <label key={it.id} className="flex items-start gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-muted/40 cursor-pointer" data-testid={`inbox-reply-attach-item-${it.id.replace(/[^a-zA-Z0-9_-]/g, "_")}`}>
                        <input type="checkbox" checked={picked.has(it.id)} onChange={() => toggle(it)} className="mt-0.5 accent-primary" />
                        {it.kind === "pdf" ? <FilePdf size={14} className="mt-0.5 text-red-500 shrink-0" /> : <FileIcon size={14} className="mt-0.5 shrink-0" />}
                        <span className="min-w-0"><span className="block truncate">{it.label}</span>{it.meta && <span className="block text-[10px] text-muted-foreground">{it.meta}</span>}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-border p-2 flex items-center justify-between gap-2">
              <span className="text-[10px] text-muted-foreground">Up to {limits.max_files} files · {limits.max_total_mb} MB total</span>
              <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="inbox-reply-upload-btn"><UploadSimple size={13} className="mr-1" /> {uploading ? "Uploading…" : "Upload a file"}</Button>
            </div>
          </PopoverContent>
        </Popover>
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="inbox-reply-upload-input" />
      </div>
    </div>
  );
}
